import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import {
  Box,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from '@mui/material';
import { useEffect, useRef } from 'react';
import { ColumnAlignment, Row, TableInstance, TableRowProps } from 'react-table';
import { QuoteTableState } from 'shared/contexts/quotes-summary-context';
import { useDebounce } from 'shared/hooks';
import { QuotesSummaryItem } from 'shared/types';
import { QuotesSummaryTablePagination } from './quotes-summary-table-pagination';

const containerStyles: SxProps<Theme> = {
  maxHeight: '90vh',
  minHeight: '100px',
};

const sortStyles: SxProps<Theme> = {
  width: '100%',
  display: 'flex',
  alignItems: 'flex-start',
};

const getHeaderFlexAlignment = (align: ColumnAlignment) => {
  switch (align) {
    case 'center':
      return 'center';
    case 'right':
      return 'flex-end';
    default:
      return 'flex-start';
  }
};

interface QuotesSummaryTableProps {
  instance: TableInstance<QuotesSummaryItem>;
  setQuoteTableState: React.Dispatch<React.SetStateAction<QuoteTableState>>;
  totalCount: number;
}
export function QuotesSummaryTable({ instance, totalCount, setQuoteTableState }: QuotesSummaryTableProps) {
  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, page, state } = instance;

  const firstRender = useRef(true);
  const debouncedState = useDebounce(state, 500);

  useEffect(() => {
    const { pageIndex, pageSize, sortBy, filters, globalFilter, hiddenColumns } = debouncedState;
    if (!firstRender.current) {
      setQuoteTableState(prevState => {
        return {
          ...prevState,
          tableState: {
            ...prevState.tableState,
            pageIndex,
            pageSize,
            sortBy,
            filters,
            globalFilter,
            hiddenColumns,
          },
        };
      });
    }
    firstRender.current = false;
  }, [debouncedState, setQuoteTableState]);

  const headers = headerGroups.map(headerGroup => (
    <TableRow {...headerGroup.getHeaderGroupProps()}>
      {headerGroup.headers.map(column => (
        <TableCell
          align={column.align}
          {...column.getHeaderProps({
            style: {
              width: column.width,
              minWidth: column.minWidth,
            },
          })}
        >
          <Box
            component='span'
            sx={sortStyles}
            {...column.getSortByToggleProps({ style: { justifyContent: getHeaderFlexAlignment(column.align) } })}
          >
            {column.render('Header')}
            {column.isSorted ? column.isSortedDesc ? <ArrowDropDown /> : <ArrowDropUp /> : null}
          </Box>
        </TableCell>
      ))}
    </TableRow>
  ));
  const rows = page.length
    ? page.map((row, i) => {
        prepareRow(row);
        const { key, ...rest } = row.getRowProps();
        return <QuotesSummaryTableRow key={key} row={row} {...rest} />;
      })
    : [
        <TableRow key='no-data'>
          <TableCell colSpan={16}>
            <Typography>No quotes were found.</Typography>
          </TableCell>
        </TableRow>,
      ];
  return (
    <div>
      <TableContainer sx={containerStyles}>
        <Table size='small' stickyHeader {...getTableProps()}>
          <TableHead>{headers}</TableHead>
          <TableBody {...getTableBodyProps()}>{rows}</TableBody>
        </Table>
      </TableContainer>
      <QuotesSummaryTablePagination instance={instance} totalCount={totalCount} />
    </div>
  );
}

interface QuotesSummaryTableRowProps extends TableRowProps {
  row: Row<QuotesSummaryItem>;
}

function QuotesSummaryTableRow({ row, ...props }: QuotesSummaryTableRowProps) {
  return (
    <TableRow {...props}>
      {row.cells.map(cell => (
        <TableCell align={cell.column.align} {...cell.getCellProps()}>
          {cell.render('Cell')}
        </TableCell>
      ))}
    </TableRow>
  );
}
